import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { ErrorBoundary } from '@/components/error-boundary';
import { ScreenLoading } from '@/components/loading';
import { RequireAdmin } from '@/components/require-admin';
import { useMeFresh } from '@/hooks/use-applications';
import { ClerkGate, ClerkSsoCallback } from '@/lib/auth';
import { fontSans, makePalette } from '@/lib/theme';
import { AdminInventoryPage } from '@/pages/admin-inventory-page';
import { AguardandoPage } from '@/pages/aguardando-page';
import { AuditCriteriaPage } from '@/pages/audit-criteria-page';
import { AuditPage } from '@/pages/audit-page';
import { CatalogHomologRedirect, CatalogPage } from '@/pages/catalog-page';
import { CostsPage } from '@/pages/costs-page';
import { DetailPage } from '@/pages/detail-page';
import { DocumentsPage } from '@/pages/documents-page';
import { EditApplicationPage } from '@/pages/edit-application-page';
import { InternalPage } from '@/pages/internal-page';
import { LoginPage } from '@/pages/login-page';
import { PresentationPage } from '@/pages/presentation-page';
import { PrimeiroAcessoPage } from '@/pages/primeiro-acesso-page';
import { RegisterPage } from '@/pages/register-page';
import { StacksPage } from '@/pages/stacks-page';
import { UsersPage } from '@/pages/users-page';
import { clerkEnabled, useSessionStore } from '@/store/session';
import { useScreenProps, useThemeStore } from '@/store/theme';

// Gate de autenticação: sem sessão → volta para /login.
function DevGate({ children }: Readonly<{ children: ReactElement }>) {
  const signedIn = useSessionStore((s) => s.signedIn);
  if (!signedIn) return <Navigate to="/login" replace />;
  return children;
}

// clerkEnabled é constante (build-time), então o componente escolhido nunca
// muda entre renders — sem violar a ordem de hooks.
const RequireAuth = clerkEnabled ? ClerkGate : DevGate;

// Falha ao carregar /api/me: tela mínima com retry — nunca spinner infinito.
function GateErrorScreen({ onRetry }: Readonly<{ onRetry: () => void }>) {
  const sp = useScreenProps();
  const p = makePalette(sp.dark);
  return (
    <div
      style={{
        width: '100%',
        minHeight: 'var(--app-vh)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: p.bg,
        fontFamily: fontSans,
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: p.surface,
          border: `1px solid ${p.border}`,
          borderRadius: 14,
          padding: 28,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: p.text, marginBottom: 8 }}>
          Falha ao carregar seu perfil
        </div>
        <div style={{ fontSize: 13, color: p.muted, lineHeight: 1.6, marginBottom: 20 }}>
          Não foi possível verificar seu acesso. Verifique sua conexão e tente novamente.
        </div>
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: '10px 20px',
            borderRadius: 10,
            border: 'none',
            background: sp.accent,
            color: '#fff',
            fontWeight: 600,
            fontSize: 13,
            fontFamily: fontSans,
            cursor: 'pointer',
          }}
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );
}

// Status que NÃO entram no app. `inactive` entrou junto com a regra do RH: sem
// ele, a conta desativada passava por este gate e só descobria o bloqueio pelos
// 403 de cada requisição — o usuário via telas de erro em vez de uma explicação.
const BLOCKED_STATUSES = new Set(['pending', 'rejected', 'inactive']);

// Gate de acesso (status + onboarding), roda DEPOIS da sessão (RequireAuth):
// sem matrícula → /primeiro-acesso; status bloqueado → /aguardando.
// Usa a query ['me'] fresca (useMeFresh) para nunca rotear com cache velho.
function AccessGate({ children }: Readonly<{ children: ReactElement }>) {
  const dark = useThemeStore((s) => s.dark);
  const { data: me, isError, refetch } = useMeFresh();

  if (!me) {
    if (isError) return <GateErrorScreen onRetry={() => void refetch()} />;
    return <ScreenLoading dark={dark} />;
  }
  // O onboarding vem PRIMEIRO, e a ordem é a correção de um beco sem saída: a
  // conciliação com o RH desativa quem está sem matrícula, e com a checagem de
  // status na frente a pessoa era mandada para /aguardando — tela que não oferece
  // caminho nenhum. A API acompanha (`/api/me/onboarding` passa pelo gate de
  // status), então vincular a matrícula resolve e o job reativa a conta.
  if (me.needsOnboarding) return <Navigate to="/primeiro-acesso" replace />;
  if (BLOCKED_STATUSES.has(me.status)) return <Navigate to="/aguardando" replace />;
  return children;
}

// Barreira de erro por rota: exceção de render numa tela mostra o fallback só
// naquela rota, sem apagar o app. A key na location remonta o boundary a cada
// navegação, senão o usuário ficaria preso no fallback ao trocar de tela.
function RouteBoundary({ children }: Readonly<{ children: ReactElement }>) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary key={pathname} label={`rota ${pathname}`}>
      {children}
    </ErrorBoundary>
  );
}

// Rota autenticada sem gate de acesso: /primeiro-acesso e /aguardando, que são
// justamente as telas de quem ainda não passou pelo AccessGate.
function AuthOnly({ children }: Readonly<{ children: ReactElement }>) {
  return (
    <RouteBoundary>
      <RequireAuth>{children}</RequireAuth>
    </RouteBoundary>
  );
}

// Rota protegida completa: sessão (RequireAuth) + aprovação/onboarding
// (AccessGate). /primeiro-acesso e /aguardando ficam só sob RequireAuth.
// O boundary fica por fora dos gates, então cobre também erro de render deles.
function Protected({ children }: Readonly<{ children: ReactElement }>) {
  return (
    <RouteBoundary>
      <RequireAuth>
        <AccessGate>{children}</AccessGate>
      </RequireAuth>
    </RouteBoundary>
  );
}

// Rota administrativa: tudo do Protected (sessão + aprovação + onboarding) e
// SÓ ENTÃO a checagem de papel (RequireAdmin). Quem não é admin nem monta a
// tela: sem request de dados internos, sem formulário preenchido em vão.
function AdminOnly({ children }: Readonly<{ children: ReactElement }>) {
  return (
    <Protected>
      <RequireAdmin>{children}</RequireAdmin>
    </Protected>
  );
}

export function App() {
  const dark = useThemeStore((s) => s.dark);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
  }, [dark]);

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/catalog" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/sso-callback"
        element={clerkEnabled ? <ClerkSsoCallback /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/primeiro-acesso"
        element={
          <AuthOnly>
            <PrimeiroAcessoPage />
          </AuthOnly>
        }
      />
      <Route
        path="/aguardando"
        element={
          <AuthOnly>
            <AguardandoPage />
          </AuthOnly>
        }
      />
      <Route
        path="/catalog"
        element={
          <Protected>
            <CatalogPage />
          </Protected>
        }
      />
      {/* Rotas antigas: "Favoritos" e "Meus apps" saíram do menu — favoritar
          virou ordenação DENTRO do catálogo (favoritos no topo). Redirecionamos
          explicitamente em vez de deixar cair no catch-all para quem tem link
          salvo/favorito do navegador chegar no catálogo sem passar por
          "página não encontrada". */}
      <Route path="/favorites" element={<Navigate to="/catalog" replace />} />
      <Route path="/mine" element={<Navigate to="/catalog" replace />} />
      {/* Rota antiga: a aba virou preferência persistida — redireciona. */}
      <Route
        path="/catalog/homolog"
        element={
          <Protected>
            <CatalogHomologRedirect />
          </Protected>
        }
      />
      <Route
        path="/admin/apresentacao"
        element={
          <AdminOnly>
            <PresentationPage />
          </AdminOnly>
        }
      />
      <Route
        path="/app/:slug"
        element={
          <Protected>
            <DetailPage />
          </Protected>
        }
      />
      <Route
        path="/admin/inventory"
        element={
          <AdminOnly>
            <AdminInventoryPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/gastos"
        element={
          <AdminOnly>
            <CostsPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/internal"
        element={
          <AdminOnly>
            <InternalPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/documents"
        element={
          <AdminOnly>
            <DocumentsPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/stacks"
        element={
          <AdminOnly>
            <StacksPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/users"
        element={
          <AdminOnly>
            <UsersPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <AdminOnly>
            <AuditPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/audit/criteria"
        element={
          <AdminOnly>
            <AuditCriteriaPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/applications/new"
        element={
          <AdminOnly>
            <RegisterPage />
          </AdminOnly>
        }
      />
      <Route
        path="/admin/applications/:slug/edit"
        element={
          <AdminOnly>
            <EditApplicationPage />
          </AdminOnly>
        }
      />
      <Route path="*" element={<Navigate to="/catalog" replace />} />
    </Routes>
  );
}
